<script setup lang="ts">
import { computed } from "vue";
import { hasMarkdown, parseMarkdown } from "../../helpers/markdown";

interface Props {
  /** Raw string that may contain `**bold**`, `*italic*`, and `[label](url)`. */
  content?: string;
  /** Wrapper element the segments are rendered into (e.g. "span", "p"). */
  tag?: string;
}

const props = withDefaults(defineProps<Props>(), {
  content: "",
  tag: "span",
});

/**
 * Detect markdown from the content itself: only strings that actually contain a
 * supported token are parsed, everything else renders as plain text. Parsing
 * into typed segments and rendering real elements through interpolation (never
 * `v-html`) keeps the supported markdown subset XSS-safe.
 */
const isMarkdown = computed(() => hasMarkdown(props.content));
const segments = computed(() =>
  isMarkdown.value ? parseMarkdown(props.content) : []
);
</script>

<template>
  <component :is="tag">
    <template v-if="isMarkdown">
      <template v-for="(segment, index) in segments" :key="index">
        <strong v-if="segment.type === 'strong'">{{ segment.text }}</strong>
        <em v-else-if="segment.type === 'em'">{{ segment.text }}</em>
        <a
          v-else-if="segment.type === 'link'"
          :href="segment.href"
          target="_blank"
          rel="noopener noreferrer"
          >{{ segment.text }}</a
        >
        <template v-else>{{ segment.text }}</template>
      </template>
    </template>
    <template v-else>{{ content }}</template>
  </component>
</template>
